import { useEffect, useState } from 'react'
import { Button } from '../components/ui/button'
import { Card } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { NotificationsList } from '../components/notifications/NotificationsList'
import { NotificationForm } from '../components/notifications/NotificationForm'
import { useCreateAndSendNotification } from '../hooks/useNotifications'
import { CreateNotificationDto, NotificationType } from '@tco/shared-types'
import { Plus, Filter, X } from 'lucide-react'

export function Notifications() {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Filters state
  const [filters, setFilters] = useState<{
    type?: NotificationType
    is_sent?: boolean
    search?: string
  }>({})
  const [searchInput, setSearchInput] = useState('')

  // Debounce текстового поиска, чтобы не дёргать API на каждый символ
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput.trim() || undefined }))
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const createAndSendMutation = useCreateAndSendNotification()

  const handleCreateNotification = async (data: CreateNotificationDto, sendImmediately: boolean) => {
    try {
      if (sendImmediately) {
        // Create and send immediately
        await createAndSendMutation.mutateAsync(data)
        alert('Уведомление создано и отправлено!')
      } else {
        // Just create (will be sent later or scheduled)
        // TODO: Use separate create mutation if needed
        alert('Уведомление создано (будет отправлено позже)')
      }
      setShowCreateDialog(false)
    } catch (err) {
      alert('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'))
    }
  }

  const handleClearFilters = () => {
    setFilters({})
    setSearchInput('')
  }

  const hasActiveFilters = Object.keys(filters).some(
    (key) => filters[key as keyof typeof filters] !== undefined
  )

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Уведомления</h1>
          <p className="text-muted-foreground">
            Управление push-уведомлениями для сотрудников
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Фильтры
            {hasActiveFilters && (
              <span className="ml-2 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {Object.keys(filters).filter((k) => filters[k as keyof typeof filters]).length}
              </span>
            )}
          </Button>

          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Создать уведомление
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Фильтры</h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-1" />
                Сбросить
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Type Filter */}
            <div className="space-y-2">
              <Label>Тип</Label>
              <Select
                value={filters.type || 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({ 
                    ...prev, 
                    type: value === 'all' ? undefined : value as NotificationType 
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все типы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все типы</SelectItem>
                  <SelectItem value={NotificationType.GENERAL}>Общее уведомление</SelectItem>
                  <SelectItem value={NotificationType.REMINDER}>Напоминание</SelectItem>
                  <SelectItem value={NotificationType.DOCUMENTS}>Документы</SelectItem>
                  <SelectItem value={NotificationType.REQUESTS}>Заявки</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Статус</Label>
              <Select
                value={
                  filters.is_sent === undefined ? 'all' : filters.is_sent ? 'sent' : 'draft'
                }
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    is_sent: value === 'all' ? undefined : value === 'sent',
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все статусы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="sent">Отправлено</SelectItem>
                  <SelectItem value="draft">Черновик</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="space-y-2">
              <Label>Поиск</Label>
              <Input
                type="text"
                placeholder="Поиск по заголовку или тексту..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Notifications List */}
      <NotificationsList filters={filters} />

      {/* Create Notification Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Создать уведомление</DialogTitle>
          </DialogHeader>
          <NotificationForm
            onSubmit={handleCreateNotification}
            isLoading={createAndSendMutation.isLoading}
            mode="create"
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
